import { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import { formatDateTime, useLanguage, useTranslation } from "@/i18n/LanguageContext";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

export default function Gyms() {
  const t = useTranslation();
  const { language } = useLanguage();
  const { page, loading, reload, changePage } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/gyms", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err)) },
  );
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const save = async (values) => {
    try {
      if (editing?.id) await api.patch(`/gyms/${editing.id}`, values);
      else await api.post("/gyms", values);
      setEditing(null);
      reload();
      toast.success(editing?.id ? t("gyms.toast.updated") : t("gyms.toast.created"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const del = async (gym) => {
    try {
      await api.delete(`/gyms/${gym.id}`);
      setConfirmDelete(null);
      reload();
      toast.success(t("gyms.toast.deleted"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <div data-testid="gyms-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("gyms.title")}
        description={t("gyms.description")}
        action={(
          <Button onClick={() => setEditing({})} className="w-full rounded-lg sm:w-auto" data-testid="gyms-create-button">
            <Plus className="mr-1 h-4 w-4" /> {t("gyms.newGym")}
          </Button>
        )}
      />

      <ResponsiveDataView
        items={page.items}
        loading={loading}
        emptyState={t("gyms.empty")}
        getRowKey={(g) => g.id}
        getRowTestId={(g) => `gym-row-${g.id}`}
        tableTestId="gyms-table"
        emptyTestId="gyms-empty"
        columns={[
          { key: "name", header: t("gyms.columns.name"), cell: (g) => g.name },
          { key: "location", header: t("gyms.columns.location"), cell: (g) => <span className="text-muted-foreground">{g.location || "-"}</span> },
          { key: "created", header: t("gyms.columns.created"), cell: (g) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(g.createdAt, language)}</span> },
        ]}
        mobileTitle={(g) => g.name}
        mobileSubtitle={(g) => g.location || t("gyms.noLocation")}
        mobileFields={[
          { label: t("common.fields.location"), value: (g) => g.location || "-" },
          { label: t("common.fields.created"), value: (g) => formatDateTime(g.createdAt, language), priority: "secondary" },
        ]}
        rowActions={[
          { key: "edit", label: t("common.actions.edit"), icon: Pencil, onClick: setEditing, testId: (g) => `edit-gym-${g.id}` },
          { key: "delete", label: t("common.actions.delete"), icon: Trash2, destructive: true, onClick: setConfirmDelete, testId: (g) => `delete-gym-${g.id}` },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "gyms-pager",
        }}
      />

      <GymDialog gym={editing} onClose={() => setEditing(null)} onSave={save} />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t("gyms.delete.title")}
        description={t("gyms.delete.description", { name: confirmDelete?.name })}
        confirmLabel={t("common.actions.delete")}
        destructive
        onConfirm={() => del(confirmDelete)}
        testId="gym-delete-dialog"
      />
    </div>
  );
}

function GymDialog({ gym, onClose, onSave }) {
  const t = useTranslation();
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");

  useEffect(() => {
    setName(gym?.name || "");
    setLocation(gym?.location || "");
  }, [gym]);

  if (!gym) return null;

  const submit = (e) => {
    e.preventDefault();
    onSave({ name: name.trim(), location: location.trim() || null });
  };

  return (
    <Dialog open={!!gym} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border" data-testid="gym-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{gym.id ? t("gyms.dialog.editTitle") : t("gyms.dialog.createTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{t("gyms.dialog.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="gym-dialog-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.location")}</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="gym-dialog-location" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-lg" data-testid="gym-dialog-cancel">{t("common.actions.cancel")}</Button>
            <Button type="submit" className="rounded-lg" data-testid="gym-dialog-save">{t("common.actions.save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
